/**
 * Frontend Observability Service
 * 
 * Provides structured event logging that integrates with the backend's
 * Loki/Grafana observability system. All events are sent through Socket.IO
 * and logged alongside backend events for unified monitoring.
 * 
 * Usage:
 *   import { obs, ObsEvent } from '../services/observability';
 *   
 *   // Set session context (called on connection_established)
 *   obs.setSession(userId, userName);
 *   
 *   // Set call context (called when entering video/audio call)
 *   obs.setCallContext(creatorId, creatorName, 'video', 'CUSTOM');
 *   
 *   // Emit events
 *   obs.emit(ObsEvent.DEEPGRAM_CONNECTED, { connection_ms: 150 });
 */

import { getSocket } from '../socket';

// Session context - set once on connection, used for all events
let sessionContext = {
    user_id: null,
    user_name: null,
};

// Per-call context - set when entering a call
let callContext = {
    creator_id: null,
    creator_name: null,
    mode: null,  // "video" or "audio"
    session_mode: null,  // "FULL" or "CUSTOM" (HeyGen session mode)
};

// Active turn tracking for latency calculations
let activeTurn = {
    turn_id: null,
    turn_start_time: null,  // Timestamp when user's last audio byte was sent
};

// Turn id of the AI reply we are currently allowed to play.
// Cleared on barge-in so late TTS chunks for the previous turn get dropped.
let validAiTurnId = null;

/**
 * Event types matching backend EventType enum
 */
export const ObsEvent = {
    // Socket Connection
    SOCKET_CONNECTED: "SOCKET_CONNECTED",
    SOCKET_DISCONNECTED: "SOCKET_DISCONNECTED",
    SOCKET_CONNECTION_DROPPED: "SOCKET_CONNECTION_DROPPED",
    SOCKET_RECONNECTED: "SOCKET_RECONNECTED",
    
    // Deepgram STT
    DEEPGRAM_CONNECTED: "DEEPGRAM_CONNECTED",
    DEEPGRAM_DISCONNECTED: "DEEPGRAM_DISCONNECTED",
    DEEPGRAM_CONNECTION_ERROR: "DEEPGRAM_CONNECTION_ERROR",
    DEEPGRAM_RETRY: "DEEPGRAM_RETRY",
    DEEPGRAM_AUDIO_CHUNK_SENT: "DEEPGRAM_AUDIO_CHUNK_SENT",
    DEEPGRAM_AUDIO_CHUNK_DROPPED: "DEEPGRAM_AUDIO_CHUNK_DROPPED",
    DEEPGRAM_TURN_DETECTED: "DEEPGRAM_TURN_DETECTED",
    DEEPGRAM_TRANSCRIPTION_COMPLETE: "DEEPGRAM_TRANSCRIPTION_COMPLETE",
    
    // LiveKit Room
    LIVEKIT_ROOM_CONNECTED: "LIVEKIT_ROOM_CONNECTED",
    LIVEKIT_ROOM_DISCONNECTED: "LIVEKIT_ROOM_DISCONNECTED",
    LIVEKIT_CONNECTION_QUALITY: "LIVEKIT_CONNECTION_QUALITY",
    
    // HeyGen/Avatar
    HEYGEN_SPEAK_COMMAND: "HEYGEN_SPEAK_COMMAND",
    HEYGEN_SPEAK_STARTED: "HEYGEN_SPEAK_STARTED",
    HEYGEN_SPEAK_ENDED: "HEYGEN_SPEAK_ENDED",
    HEYGEN_KEEP_ALIVE_SENT: "HEYGEN_KEEP_ALIVE_SENT",
    HEYGEN_KEEP_ALIVE_SUCCESS: "HEYGEN_KEEP_ALIVE_SUCCESS",
    HEYGEN_KEEP_ALIVE_FAILED: "HEYGEN_KEEP_ALIVE_FAILED",
    
    // Audio Call
    AUDIO_CALL_STARTED: "AUDIO_CALL_STARTED",
    AUDIO_CALL_ENDED: "AUDIO_CALL_ENDED",
    
    // Video Call
    VIDEO_CALL_STARTED: "VIDEO_CALL_STARTED",
    VIDEO_CALL_ENDED: "VIDEO_CALL_ENDED",
    
    // End-to-End Latency
    E2E_TURN_START: "E2E_TURN_START",
    E2E_AUDIO_TTFB: "E2E_AUDIO_TTFB",
    E2E_VIDEO_TTFB: "E2E_VIDEO_TTFB",
    E2E_TURN_COMPLETE: "E2E_TURN_COMPLETE",
};

/**
 * Main observability object
 */
export const obs = {
    /**
     * Set session context (called on connection_established)
     * @param {string} userId - User's Clerk ID
     * @param {string} userName - User's display name
     */
    setSession(userId, userName) {
        sessionContext = { user_id: userId, user_name: userName };
    },
    
    /**
     * Set call context (called when entering video/audio call)
     * @param {string} creatorId - Creator's Clerk ID
     * @param {string} creatorName - Creator's display name  
     * @param {string} mode - "video" or "audio"
     * @param {string} [sessionMode] - HeyGen session mode: "FULL" or "CUSTOM"
     */
    setCallContext(creatorId, creatorName, mode, sessionMode = null) {
        callContext = { creator_id: creatorId, creator_name: creatorName, mode, session_mode: sessionMode };
    },
    
    /**
     * Clear call context (called when leaving call)
     */
    clearCallContext() {
        callContext = { creator_id: null, creator_name: null, mode: null, session_mode: null };
        activeTurn = { turn_id: null, turn_start_time: null };
        validAiTurnId = null;
    },
    
    /**
     * Start a new turn (called when user's last audio byte is detected)
     * Returns the turn_id for correlation
     * @returns {string} The generated turn_id
     */
    startTurn() {
        activeTurn = {
            turn_id: crypto.randomUUID(),
            turn_start_time: Date.now(),
        };
        validAiTurnId = activeTurn.turn_id;

        this.emit(ObsEvent.E2E_TURN_START, {
            turn_id: activeTurn.turn_id,
            turn_start_time: activeTurn.turn_start_time,
        });

        return activeTurn.turn_id;
    },

    invalidateAiTurn() {
        validAiTurnId = null;
    },

    isValidAiTurn(turnId) {
        // Spontaneous server messages (e.g. initial greeting) arrive with
        // turn_id=null before any user turn has started. Allow them only when
        // no user turn is active locally either — not a barge-in bypass.
        if (turnId == null && validAiTurnId == null) return true;
        return !!turnId && turnId === validAiTurnId;
    },
    
    /**
     * Get current turn context
     * @returns {Object} Current turn_id and turn_start_time
     */
    getTurn() {
        return { ...activeTurn };
    },
    
    /**
     * Calculate elapsed time since turn start
     * @returns {number|null} Milliseconds since turn start, or null if no active turn
     */
    getElapsedSinceTurnStart() {
        if (!activeTurn.turn_start_time) return null;
        return Date.now() - activeTurn.turn_start_time;
    },
    
    /**
     * End current turn
     * @param {Object} metadata - Additional metadata for turn complete event
     */
    endTurn(metadata = {}) {
        if (activeTurn.turn_id) {
            const totalMs = this.getElapsedSinceTurnStart();
            this.emit(ObsEvent.E2E_TURN_COMPLETE, {
                turn_id: activeTurn.turn_id,
                duration_ms: totalMs,
                ...metadata,
            });
        }
        activeTurn = { turn_id: null, turn_start_time: null };
    },
    
    /**
     * Emit an observability event to the backend
     * @param {string} event - Event type from ObsEvent
     * @param {Object} metadata - Additional event-specific data
     */
    emit(event, metadata = {}) {
        const socket = getSocket();
        if (socket?.connected) {
            socket.emit("obs_event", { 
                event, 
                metadata: {
                    ...sessionContext,
                    ...callContext,
                    turn_id: activeTurn.turn_id,
                    ...metadata,
                    timestamp: Date.now()
                }
            });
        } else {
            // Queue for later? For now just log to console
            console.debug('[Obs]', event, metadata);
        }
    },
    
    /**
     * Emit event with latency calculation from turn start
     * @param {string} event - Event type
     * @param {string} latencyField - Name of the latency field (e.g., 'e2e_ttfb_ms')
     * @param {Object} metadata - Additional metadata
     */
    emitWithLatency(event, latencyField, metadata = {}) {
        const latency = this.getElapsedSinceTurnStart();
        this.emit(event, {
            [latencyField]: latency,
            turn_id: activeTurn.turn_id,
            ...metadata,
        });
    },
};

/**
 * Latency tracker helper for measuring operation durations
 */
export class LatencyTracker {
    constructor() {
        this.startTime = null;
        this.markers = {};
    }
    
    /**
     * Start tracking
     */
    start() {
        this.startTime = Date.now();
        return this;
    }
    
    /**
     * Mark a point in time
     * @param {string} name - Marker name
     */
    mark(name) {
        this.markers[name] = Date.now();
        return this;
    }
    
    /**
     * Get elapsed time since start
     * @returns {number} Milliseconds since start
     */
    elapsed() {
        return this.startTime ? Date.now() - this.startTime : 0;
    }
    
    /**
     * Get time between start and a marker
     * @param {string} name - Marker name
     * @returns {number|null} Milliseconds or null if marker doesn't exist
     */
    toMarker(name) {
        if (!this.startTime || !this.markers[name]) return null;
        return this.markers[name] - this.startTime;
    }
    
    /**
     * Get time between two markers
     * @param {string} from - Start marker name
     * @param {string} to - End marker name
     * @returns {number|null} Milliseconds or null if markers don't exist
     */
    between(from, to) {
        if (!this.markers[from] || !this.markers[to]) return null;
        return this.markers[to] - this.markers[from];
    }
}

export default obs;
